import React from 'react';
import { Grid, Link, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, DialogContentText } from '@mui/material';
import GadgetLoader from '../../Loaders/GadgetLoader';
import EnhancedDataGrid from '../../Common/EnhancedDataGrid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import RouteToEmployeeIcon from '@mui/icons-material/AssignmentInd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { formatNumber, formatDate, getFileIcon, getDisplayName } from '../../Common/Utilities';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CircularProgress from '@mui/material/CircularProgress';


const TopRightGadget = ({
	xsVal,
	gridCss,
	keyVal,
	thisRef,
	fontStyle,
	cellStyle,
	hasSearchedOnce
}) => {
	const [resolveDialogOpen, setResolveDialogOpen] = React.useState(false);
	const [resolveRow, setResolveRow] = React.useState(null);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);

	const { modelDefId } = thisRef.state;
	const effectiveModelDefId = Number(thisRef.props.modelDefId ?? modelDefId);
	const tableOptionsTopRight = {
		minBodyHeight: 230,
		maxBodyHeight: 230,
		headerStyle: fontStyle,
		searchFieldStyle: fontStyle,
		showTextRowsSelected: false,
		selection: true,
		showSelectAllCheckbox: false,
		selectionProps: rowData => ({
			color: 'default'
		})
	};
	let that = thisRef;

	// Shared text renderer:
	// - If text has spaces: allow wrapping (2 lines) then ellipsis
	// - If text is a single long token: keep one line and ellipsis
	const renderSmartText = React.useCallback((text) => {
		const value = (text ?? '').toString();
		const hasWhitespace = /\s/.test(value);

		const style = hasWhitespace
			? {
				display: '-webkit-box',
				WebkitLineClamp: 2,
				WebkitBoxOrient: 'vertical',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				wordBreak: 'normal',        // break only on spaces
				overflowWrap: 'break-word',
				whiteSpace: 'normal',
				lineHeight: 1.3,
			}
			: {
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',       // long single token -> ellipsis
				display: 'block',
				lineHeight: 1.3,
			};

		return (
			<Tooltip title={value} placement="top-start">
				<span style={style}>{value}</span>
			</Tooltip>
		);
	}, []);

	const isMissingVendorIdentifier = React.useCallback((row) => {
		// These rows don't have a vendor identifier / mapping; user must either fix manually or create a model.
		// vendormappingid + statusid are returned by the backend DTO.
		return (row?.invoiceDocumentStatusId === 10) && (!row?.vendorMappingId);
	}, []);

	const openResolveDialog = React.useCallback((row) => {
		setResolveRow(row);
		setResolveDialogOpen(true);
	}, []);

	const closeResolveDialog = React.useCallback(() => {
		setResolveDialogOpen(false);
		setResolveRow(null);
	}, []);

	const continueMakeCorrections = React.useCallback(() => {
		if (!resolveRow) return;
		closeResolveDialog();
		//thisRef.openInvoiceFromRow(resolveRow, 'unmatchedInvoices', 'MakeCorrections');
		thisRef.openInvoiceFromRow(resolveRow, '1', 'MakeCorrections');
	}, [resolveRow, closeResolveDialog, thisRef]);

	const continueMapModel = React.useCallback(() => {
		if (!resolveRow) return;
		closeResolveDialog();
		// Reuse existing dashboard handler that duplicates invoice for model setup.
		thisRef.invoiceDocument_mapmodel_clickhandler({
			preventDefault: () => { },
			target: {
				dataset: {
					rowid: String(resolveRow.rowId),
					source: 'unmatchedInvoices',
					mode: 'NewSample'
				}
			}
		});
	}, [resolveRow, closeResolveDialog, thisRef]);

	const openDeleteConfirmDialog = React.useCallback(() => {
		const selectedRows = thisRef.selectedRows || [];
		const deleteCandidates = selectedRows.filter(row => row?.invoiceDocumentId);

		if (deleteCandidates.length === 0) {
			thisRef.bulkDeleteUnmatchedInvoices_clickHandler({
				preventDefault: () => { },
				currentTarget: { dataset: { gadgetnumber: '2' } }
			});
			return;
		}

		setDeleteConfirmOpen(true);
	}, [thisRef]);

	const closeDeleteConfirmDialog = React.useCallback(() => {
		setDeleteConfirmOpen(false);
	}, []);

	const confirmDeleteUnmatchedInvoices = React.useCallback((event) => {
		setDeleteConfirmOpen(false);
		thisRef.bulkDeleteUnmatchedInvoices_clickHandler(event);
	}, [thisRef]);

	const onUnmatchedRowClick = React.useCallback((params, event) => {
		const row = params?.row;
		if (!row) return;

		// Keep dashboard's "don't open" guards (links/buttons/checkbox selection).
		const target = event?.target;
		if (target && typeof target.closest === 'function') {
			if (
				target.closest('a') ||
				target.closest('button') ||
				target.closest('[role="checkbox"]') ||
				target.closest('.MuiDataGrid-checkboxInput')
			) {
				return;
			}
		}

		if (thisRef.isInvoiceRowReloading?.(row.invoiceDocumentId)) return;

		if (isMissingVendorIdentifier(row)) {
			openResolveDialog(row);
			return;
		}

		thisRef.handleDashboardRowClick(2, params, event);
	}, [thisRef, isMissingVendorIdentifier, openResolveDialog]);

	const isWeeksToFilterSearchActive = React.useMemo(() => {
		const text = thisRef.state.filterText[2];
		const prev = thisRef.state.prevSearchText[2];
		const weeks = thisRef.state.weeksToFilter2;
		return (
			(text !== '' && weeks !== 0) ||
			(weeks === 0 && text === prev && text.trim() !== '')
		);
	}, [thisRef.state.filterText[2], thisRef.state.prevSearchText[2], thisRef.state.weeksToFilter2]);
	const serverProps = React.useMemo(() => { return thisRef.state.serverMode[2] && thisRef.state.weeksToFilter2 === 0 ? { filterMode: "server" } : {} }, [thisRef.state.serverMode[2], thisRef.state.weeksToFilter2]);
	const toolTipMessage = React.useMemo(() => { return (thisRef.state.serverMode[2] && thisRef.state.weeksToFilter2 === 0) ? thisRef.toolTipMessage : '' }, [thisRef.state.weeksToFilter2, thisRef.state.serverMode[2]]);
	const deleteSelectionCount = (thisRef.selectedRows || []).filter(row => row?.invoiceDocumentId).length;
	return <Grid item xs={xsVal} className="fade-in" key={keyVal}>
		<div style={{ position: "relative", ...gridCss }}>
			<GadgetLoader loading={thisRef.state.gadgetLoading[2]} />
			<Dialog
				open={resolveDialogOpen}
				onClose={closeResolveDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Resolve invoice</DialogTitle>
				<DialogContent>
					<Typography sx={{ mt: 1 }}>
						This invoice is missing vendor identifier. Choose how you want to continue.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={continueMakeCorrections} variant="outlined">Make Corrections</Button>
					<Button onClick={continueMapModel} variant="contained">Map Model</Button>
					<Button onClick={closeResolveDialog} color="inherit">Cancel</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={deleteConfirmOpen}
				onClose={closeDeleteConfirmDialog}
				aria-labelledby="delete-confirm-dialog-title"
			>
				<DialogTitle style={{ cursor: 'move' }} id="delete-confirm-dialog-title">
					Delete
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{deleteSelectionCount > 1
							? `Are you sure you want to delete these ${deleteSelectionCount} invoices?`
							: 'Are you sure you want to delete this invoice?'}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={(event) => confirmDeleteUnmatchedInvoices(event)}
						color="primary"
						data-gadgetnumber={2}
					>
						Yes
					</Button>
					<Button autoFocus onClick={closeDeleteConfirmDialog} color="primary">
						No
					</Button>
				</DialogActions>
			</Dialog>
			<EnhancedDataGrid rows={thisRef.state.filteredUnmatchedInvoices}
				disableColumnMenu
				pagination
				title="Unmatched/Discrepant Invoices"
				gridRef={thisRef.invoiceUnmatchedGridRef}
				pageSize={thisRef.state.pageSize}
				toolTipMessage={toolTipMessage}
				hasSearchedOnce={hasSearchedOnce}
				{...serverProps}
				onSearchFocus={() => thisRef.handleSearchFocus(2)}
				searchCallback={
					null
					//(searchText) => {
					//const trimmedText = (searchText || "").trim();
					//const prevText = thisRef.state.prevSearchText[2];

					//if (trimmedText === "" && prevText !== "") {
					//	thisRef.setState((prevState) => ({
					//		prevSearchText: {
					//			...prevState.prevSearchText,
					//			2: ""
					//		}
					//	}), () => {
					//		thisRef.fetchDashboard(2, thisRef.state.weeksToFilter2 ?? 0, searchText);
					//	});
					//	return;
					//}

					//if (trimmedText !== "" && trimmedText !== prevText) {
					//	thisRef.setState((prevState) => ({
					//		prevSearchText: {
					//			...prevState.prevSearchText,
					//			2: trimmedText
					//		}
					//	}), () => {
					//		thisRef.fetchDashboard(2, thisRef.state.weeksToFilter2 ?? 0, searchText);
					//	});
					//}
					//}
				}

				setFilterText={(text) => { thisRef.setFilterText(2, text) }}
				filterText={thisRef.state.filterText[2]}
				isWeeksToFilterSearchActive={isWeeksToFilterSearchActive}
				sortModel={thisRef.state.sortModel[2]}
				onSortModelChange={(model) => thisRef.setSortModel(2, model)}
				rowsPerPageOptions={thisRef.state.rowsPerPage}

				//showSearchTooltip={
				//	thisRef.state.switchToFullScreen &&
				//	thisRef.state.showGadget === 2 &&
				//	thisRef.state.showSearchTooltip 
				//}				
				footerMessage={''}
				showSearchTooltip={false}
				getRowClassName={
					thisRef.getDashboardRowClassName

				}
				onRowClick={onUnmatchedRowClick}
				sx={{
					'& .MuiDataGrid-cell[data-field="isFavorite"]': {
						paddingLeft: '0px !important',
						paddingRight: '0px !important',
					},

					'& .MuiDataGrid-columnHeader[data-field="isFavorite"]': {
						paddingLeft: '0px !important',
						paddingRight: '0px !important',
					},

					'& .MuiDataGrid-cellCheckbox': {
						paddingLeft: '8px',
						paddingRight: '6px',
					},
				}}
				columns={[
					{
						headerName: '',
						field: 'isFavorite',
						width: 34,
						minWidth: 34,
						cellStyle: { ...cellStyle, padding: 0 },
						cellClassName: 'favorite-cell',
						renderCell: rowData => {
							const isFav = rowData.row.isFavorite === "Y";
							return (
								<Tooltip title={isFav ? "Remove Favorite" : "Mark as Favorite"}>
									<IconButton
										size="small"
										onClick={() => thisRef.toggleFavoriteStatus(rowData.row)}
										sx={{
											width: 32,
											height: 32,
											padding: 0,
											borderRadius: '50%',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										{isFav ? (
											<StarIcon sx={{ color: '#f4c542', fontSize: 20 }} />
										) : (
											<StarBorderIcon sx={{ color: '#666', fontSize: 20 }} />
										)}
									</IconButton>
								</Tooltip>
							);
							}
						},
					
						...(  
							thisRef.state.isExpanded
								? [
									{
									headerName: 'Vendor',
									field: 'vendorNameCode',
									align: 'left',
									headerAlign: 'left',
									minWidth:120,
									cellStyle: cellStyle,
									...(thisRef.state.showGadget === 2 ? { flex: 1 } : { width: 100 }),
									renderCell: (rowData) => {
										const row = rowData.row;
										const value = row.vendorNameCode || '';

										if (value) {
											return renderSmartText(value);
										}

										if (row.vendorIdentifier) {
											return (
												<Tooltip
													title={`Vendor Identifier: ${row.vendorIdentifier}`}
													placement="top-start"
												>
													<span
														style={{
															color: '#f97316',
															fontWeight: 500,
															whiteSpace: 'normal',
															wordBreak: 'break-word',
															cursor: 'default'
														}}
													>
														{row.vendorIdentifier}
													</span>
												</Tooltip>
											);
										}

										return '';
									}
								}
							]
							: [
								{
									headerName: 'Vendor Name',
									field: 'vendorName',
									align: 'left',
									headerAlign: 'left',
									cellStyle: cellStyle,
									...(thisRef.state.showGadget === 2 ? { flex: 1 } : { width: 100 }),
									renderCell: (rowData) => {
										const row = rowData.row;
										const vendorName = thisRef.displayVendorName(row);

										if (vendorName) {
											return renderSmartText(vendorName);
										}

										if (row.vendorIdentifier) {
											return (
												<Tooltip
													title={`Vendor Identifier: ${row.vendorIdentifier}`}
													placement="top-start"
												>
													<span
														style={{
															color: '#f97316',
															fontWeight: 500,
															whiteSpace: 'normal',
															wordBreak: 'break-word',
															cursor: 'default'
														}}
													>
														{row.vendorIdentifier}
													</span>
												</Tooltip>
											);
										}

										return '';
									}
								}
							]
					),

					...(effectiveModelDefId === 3 ? [{
							headerName: 'Media Type',
							field: 'mediaType',
							width: 100,
							cellStyle: cellStyle,
							renderCell: (rowData) => renderSmartText(rowData?.row?.mediaType || '')
						}] : []),

						...(effectiveModelDefId === 3 && thisRef.state.isExpanded ? [
							{
								headerName: 'Station',
								field: 'stationName',
								width: 100,
								align: 'left',
								headerAlign: 'left',
								cellStyle: cellStyle,
								renderCell: (rowData) => renderSmartText(rowData?.row?.stationName || '')
							},
							{
								headerName: 'Invoice Date',
								field: 'invoiceDate',
								width: 110,
								align: 'left',
								headerAlign: 'left',
								cellStyle: cellStyle,
								valueGetter: ({ value }) => (value ? new Date(value) : null),
								valueFormatter: ({ value }) => value
									? new Intl.DateTimeFormat('en-US', {
										timeZone: 'UTC',
										year: 'numeric',
										month: 'numeric',
										day: 'numeric',
									}).format(value)
									: '',
							},
							{
								headerName: 'Contract',
								field: 'contract',
								width: 100,
								align: 'left',
								headerAlign: 'left',
								cellStyle: cellStyle,
								renderCell: (rowData) => renderSmartText(rowData?.row?.contract || '')
							},
							{
								headerName: '# of Spots',
								field: 'totalSpot',
								type: 'number',
								width: 90,
								align: 'right',
								headerAlign: 'right',
								cellStyle: cellStyle,
								renderCell: (rowData) => <span style={{ display: 'block', textAlign: 'right', width: '100%' }}>{rowData.row.totalSpot ?? ''}</span>
							},
							// {
							// 	headerName: 'Source',
							// 	field: 'invoiceSource',
							// 	width: 100,
							// 	align: 'left',
							// 	headerAlign: 'left',
							// 	cellStyle: cellStyle,
							// 	renderCell: (rowData) => renderSmartText(rowData?.row?.invoiceSource || '')
							// },
						] : []),

							...(
								thisRef.state.isExpanded
									? [
										{
											headerName: 'Client',
											field: 'clientNameCode',
											flex: 1,
											minWidth: 100,
											align: 'left',
											headerAlign: 'left',
											cellStyle: cellStyle,
											renderCell: (rowData) => renderSmartText(rowData?.row?.clientNameCode || '')
										}
									]
									: []
						),
					...(effectiveModelDefId === 3 && thisRef.state.isExpanded ?
						[
							{
								headerName: 'Product',
								field: 'agencyProductCode',
								width: 100,
								align: 'left',
								headerAlign: 'left',
								cellStyle: cellStyle,
								renderCell: (rowData) => renderSmartText(rowData?.row?.agencyProductCode || '')
							},

						] : []
					),
					{
						headerName: 'Status',
						field: 'status',
						cellStyle: cellStyle,
						width: 100,
						minWidth: 100,
						renderCell: (rowData) => {
							const row = rowData.row;
							const isReloading = thisRef.isInvoiceRowReloading(row.invoiceDocumentId);

							const statusText =
								row.status +
								(row.duplicateInvoiceFlag === 'Y' ? ' (Duplicate Invoice!)' : '');

							return (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										width: '100%',
									}}
								>
									<Tooltip title={statusText || ''} placement="top-start">
										<span
											style={{
												/* Width behavior */
												flexBasis: isReloading ? '80%' : '100%',
												flexGrow: 0,
												flexShrink: 1,

												/* Text behavior */
												display: '-webkit-box',
												WebkitLineClamp: 2,
												WebkitBoxOrient: 'vertical',
												overflow: 'hidden',
												textOverflow: 'ellipsis',

												/* IMPORTANT: break only on spaces */
												wordBreak: 'normal',
												overflowWrap: 'break-word',
												whiteSpace: 'normal',

												lineHeight: 1.3,
											}}
										>
											{statusText}
										</span>
									</Tooltip>

									{/* Spinner takes space ONLY when rendered */}
									{isReloading && (
										<div
											style={{
												flexBasis: '20%',
												flexShrink: 0,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											<CircularProgress size={18} />
										</div>
									)}
								</div>
							);
						}
					},
					{
						headerName: 'File Name',
						field: 'fileName',
						//...(thisRef.state.showGadget === 2 ? { flex: 1 } : { width: 120 }),
						flex: 1,
						minWidth: 150,
						cellStyle: cellStyle,
						renderCell: (rowData) => {
							const { invoiceDocumentId, invoiceDocumentFileLocation, fileName } = rowData.row;
							const isDownloading = thisRef.state.downloadingInvoices[invoiceDocumentId];

							if (isDownloading) {
								return (
									<div
										style={{
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											width: '100%',
											height: '100%',
										}}
									>
										<CircularProgress size={18} />
									</div>
								);
							}

							if (!invoiceDocumentFileLocation) {
								return renderSmartText(fileName || '');
							}

							return (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 0,
										width: '100%',
										overflow: 'hidden',
									}}
								>
									<span
										role="button"
										tabIndex={0}
										style={{
											display: 'flex',
											alignItems: 'center',
											cursor: 'pointer',
											flexShrink: 0,
										}}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											thisRef.downloadFile(invoiceDocumentId, invoiceDocumentFileLocation);
										}}
									>
										<Tooltip title="View">
											{getFileIcon(invoiceDocumentFileLocation)}
										</Tooltip>
									</span>

									<span
										style={{
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											maxWidth: '100%',
											display: 'block',
										}}
										title={fileName}
									>
										{fileName}
									</span>
								</div>
							);
						}

					},
					...((thisRef.isAdmin && thisRef.state.isExpanded) ? [{
						headerName: 'Invoice Number',
						field: 'invoiceNumber',
						width: 120,
						cellStyle: cellStyle
					}] : []),
					{
						headerName: getDisplayName('User Status'),
						field: 'invoiceUserStatus',
						width: 100,
						minWidth: 100,
						cellStyle: cellStyle,
						renderCell: (params) => {
							const value = params.value || '';

							return (
								<Tooltip title={value} placement="top-start">
									<span
										style={{
											display: 'block',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											width: '100%',
											cursor: 'default'
										}}
									>
										{value}
									</span>
								</Tooltip>
							);
						}
					}
					,
					...((thisRef.state.isExpanded && thisRef.props.modelDefId !== 2) ? [{
							headerName: 'Month Of Service',
						field: 'serviceTermStartEndDate',
						flex: 1,
						minWidth:120,
						cellStyle: cellStyle
					}] : []),
					...((thisRef.state.isExpanded) ? [{
						headerName: 'Net Amount',
						field: 'netAmount',
						type: 'number',
						width: 130,
						align: 'right',
						headerAlign: 'right',
						renderCell: rowData => <span style={{ display: 'block', textAlign: 'right', width: '100%' }}>{formatNumber(rowData.row.netAmount || 0) + " " + (rowData.row.currencyCode || '')}</span>
					}] : []),
					...((thisRef.state.isExpanded) ? [{
						headerName: 'Tax Amount',
						field: 'taxAmount',
						type: 'number',
						width: 130,
						align: 'right',
						headerAlign: 'right',
						renderCell: rowData => <span style={{ display: 'block', textAlign: 'right', width: '100%' }}>{formatNumber(rowData.row.taxAmount || 0) + " " + (rowData.row.currencyCode || '')}</span>
					}] : []),
					{
						headerName: 'Total Invoice Amt',
						field: 'totalInvoiceAmount',
						type: 'number',
						width: 100,
						align: 'right',
						headerAlign: 'right',
						renderCell: rowData => <span style={{ display: 'block', textAlign: 'right', width: '100%' }}>{formatNumber(rowData.row.totalInvoiceAmount || 0) + " " + (rowData.row.currencyCode || '')}</span>
					},
					...(modelDefId !== 3 ? [
						{
							headerName: 'Routed To',
							field: 'routedTo',
							cellStyle: cellStyle,
							hide: true /*!thisRef.isAdmin*/,
							renderCell: (rowData) => {
								const routedToCsv = rowData.row.routedToCsv;

								const routedToList =
									routedToCsv && routedToCsv.split('|$|').length > 1
										? routedToCsv
											.split('|$|')
											.filter(Boolean)
											.map(r => r.split('|')[1])
										: [];

								const hasMultiple = routedToList.length > 0;
								const text = rowData.row.routedTo;

								const TextContent = (
									<Typography
										variant="inherit"
										sx={{
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											display: '-webkit-box',
											WebkitLineClamp: 2, // number of lines before ...
											WebkitBoxOrient: 'vertical',
											wordBreak: 'break-word'
										}}
									>
										{text}
									</Typography>
								);

								return (
									<div
										style={{
											display: 'grid',
											gridTemplateColumns: '1fr auto',
											alignItems: 'center',
											columnGap: 6,
											width: '100%'
										}}
									>
										{hasMultiple ? (
											<Tooltip
												placement="top-start"
												title={
													<ul
														style={{
															margin: 0,
															padding: '2px 2px',
															listStyleType: 'none'
														}}
													>
														{routedToList.map((name, idx) => (
															<li
																key={idx}
																style={{
																	whiteSpace: 'nowrap',
																	lineHeight: 1.4
																}}
															>
																{name}
															</li>
														))}
													</ul>
												}
											>
												<div>{TextContent}</div>
											</Tooltip>
										) : (
											TextContent
										)}

										<div style={{ display: 'flex', alignItems: 'center' }}>
											{thisRef.getRoutedToInfoColumn(rowData.row)}
										</div>
									</div>
								);
							}
						}
					] : []),

					//{
					//	headerName: ' ',
					//	field: 'comments',
					//	cellStyle: cellStyle,
					//	width: 20,
					//	resizable: false,
					//	sortable: false,
					//	renderCell: rowData => { return thisRef.getRoutedToInfoColumn(rowData.row); }
					//},
					{
						headerName: 'Date Scanned',
						field: 'invoiceDocumentScanUpdateDate',
						type: 'datetime',
						defaultSort: 'desc',
						valueGetter: ({ value }) => (value ? new Date(value) : null),
						valueFormatter: ({ value }) => value
							? new Intl.DateTimeFormat('en-US', {
								timeZone: 'UTC',
								year: 'numeric',
								month: 'numeric',
								day: 'numeric',
								hour: 'numeric',
								minute: '2-digit',
								second: '2-digit',
								hour12: true,
							}).format(value)
							: '',
						minWidth: 100,
						flex: 1,
						cellStyle: cellStyle
					}
				]}
				customcomponent={<>
					{thisRef.getOpenInFullScreenButton(2)}
					{thisRef.getRefreshButton(2)}
					{thisRef.state.deletePermission && (
						<IconButton
							variant="contained"
							onClick={openDeleteConfirmDialog}
							size="small"
							style={{ float: "right", zIndex: 1, marginTop: 10 }}
							data-gadgetnumber={2}
						>
							<Tooltip title={'Delete selected'}>
								<DeleteOutlineIcon />
							</Tooltip>
						</IconButton>
					)}
					{thisRef.state.isAllowRoutingSetup && (
						<IconButton
							variant="contained"
							onClick={() => {
								thisRef.bulkRouteToEmployee_clickHandler();
							}}

							size="small"
							style={{ float: "right", zIndex: 1, marginTop: 10 }}
						>
							<Tooltip key={1} title={'Route to employees'}>
								<RouteToEmployeeIcon key={2} />
							</Tooltip>
						</IconButton>
					)}
					{thisRef.getRoutedToFilterDropDown()}
					{thisRef.getWeeksToFilterDropDown(2, thisRef.state.filterText[2] || '')}
					{thisRef.getSearchButton(2)}
				</>
				}
				options={tableOptionsTopRight}
				onSelectionChange={(rows) => { thisRef.selectedRows = rows }}
				getRowId={(row) => row.rowId}
				quickFilterDebounceMs={thisRef.getQuickFilterDebounceMs(2)}
				disableRowSelectionOnClick
			/>
		</div>
	</Grid>


};

export default TopRightGadget;